// CartaoDoClube.js — o lugar do cartão de preço, na vitrine do livro do Clube.
//
// Na página do livro da loja, à direita da capa, mora o PrecoCard: formatos,
// preços, frete, carrinho. Na vitrine do Clube (/clube/livros/:id) o assinante
// não compra nada ali, então o cartão vira as ações de quem lê:
//
//   Ler ...................... abre o leitor (/ler/:id)
//   🔖 ....................... salva o livro em Perfil → E-books
//   Ver preços ............... a página do livro NA LOJA, com os preços
//
// ================= DO TAMANHO DO QUE ELE DIZ (11/09/2026) =================
//
// A primeira versão tinha a largura da coluna inteira, botões de ponta a ponta
// e dois parágrafos de explicação — um bloco do tamanho do cartão de preço para
// dizer "Ler". O dono pediu botões do tamanho do texto e o salvar só como
// ícone: agora o cartão tem a largura do próprio conteúdo, e o espaço que
// sobra à direita dele recebe a sinopse (ver PaginaProduto, modo "clube").
//
// ================= O QUE O ÍCONE DE SALVAR SABE =================
//
// O estado vem do servidor (GET /minha-estante/livro/:id), e não de um "salvei
// agora há pouco" guardado na tela, porque o livro pode já estar na estante
// por OUTRO motivo: comprado, ganho com o impresso, ou lido 10% pelo Clube.
// Nesses casos o ícone aparece marcado e parado — "Salvar" prometeria uma
// mudança que não acontece.
//
// Tirar da estante só é possível quando tirar TIRA (`removivel`): o livro que
// também foi lido continuaria lá, e um "remover" que não remove é pior que
// nenhum.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { message } from "../utils/toast";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

const cabecalhos = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  Accept: "application/json",
});

const CartaoDoClube = ({ ebookId, className = "" }) => {
  const [situacao, setSituacao] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [sobre, setSobre] = useState(false);

  useEffect(() => {
    let vivo = true;
    setSituacao(null);
    (async () => {
      try {
        const r = await fetch(`${API_URL}minha-estante/livro/${ebookId}`, { headers: cabecalhos() });
        if (vivo && r.ok) setSituacao(await r.json());
      } catch { /* sem a situação o ícone fica parado, e ler continua funcionando */ }
    })();
    return () => { vivo = false; };
  }, [ebookId]);

  const alternar = async () => {
    if (!situacao || ocupado) return;
    const remover = situacao.salvo && situacao.removivel;
    setOcupado(true);
    try {
      const r = await fetch(`${API_URL}minha-estante/livro/${ebookId}`, {
        method: remover ? "DELETE" : "POST",
        headers: cabecalhos(),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.error || "Não foi possível agora. Tente de novo.");
      setSituacao(d);
      setSobre(false);
      message.success(remover ? "Livro tirado dos seus E-books." : "Salvo nos seus E-books (Perfil → E-books).");
    } catch (e) {
      message.error(e.message);
    } finally {
      setOcupado(false);
    }
  };

  const progresso = situacao?.progresso;
  const pct = Math.max(0, Math.min(100, Number(progresso?.percentual) || 0));

  // Os quatro estados do botão de salvar. Ele é SÓ ÍCONE (pedido do dono), então
  // o `titulo` carrega o nome inteiro: vira a dica do mouse e o que o leitor de
  // tela lê — sem ele o botão seria um marcador mudo para quem não enxerga.
  let salvar;
  if (!situacao) {
    salvar = { icone: "bi-bookmark", titulo: "Salvar nos meus E-books", parado: true };
  } else if (situacao.salvo && situacao.removivel) {
    salvar = sobre
      ? { icone: "bi-bookmark-x", titulo: "Tirar dos meus E-books", marcado: true }
      : { icone: "bi-bookmark-check-fill", titulo: "Salvo nos seus E-books (clique para tirar)", marcado: true };
  } else if (situacao.na_estante) {
    salvar = { icone: "bi-bookmark-check-fill", titulo: "Já está nos seus E-books", parado: true, marcado: true };
  } else {
    salvar = { icone: "bi-bookmark-plus", titulo: "Salvar nos meus E-books" };
  }

  return (
    // LARGURA FIXA a partir de 640px (pedido do dono em 11/09/2026: "os botões
    // estão desorganizados"). Antes o cartão tinha a largura do próprio
    // conteúdo, e cada botão terminava num lugar diferente — três réguas
    // distintas empilhadas. Com a coluna definida, as duas fileiras terminam
    // na mesma borda: "Ler" com o marcador ao lado em cima, "Ver preços"
    // embaixo. O "Ler" (e não "Ler agora") e o salvar só como ícone, colado
    // nele, são o que o dono pediu primeiro — e tinham se perdido quando os
    // botões foram rearrumados.
    <div
      className={
        "flex w-full sm:w-[248px] flex-col rounded-xl border border-insigne-orange/35 " +
        "bg-insigne-navy-soft p-4 " + className
      }
    >
      <p className="flex items-start gap-1.5 text-sm font-bold text-white leading-snug">
        <i className="bi bi-stars mt-0.5 text-insigne-orange" /> Incluso no seu Clube Insigne
      </p>

      {/* Onde a pessoa parou — o mesmo número que a estante mostra. */}
      {progresso && pct > 0 && (
        <div className="mt-2.5 w-full">
          <p className="flex items-center justify-between gap-3 text-[11px] text-white/55">
            <span>
              Página {progresso.ultima_pagina}
              {progresso.total_paginas > 0 && ` de ${progresso.total_paginas}`}
            </span>
            <span className="font-semibold text-white/80">{pct}%</span>
          </p>
          <div className="mt-1 h-1 rounded-selo bg-white/10 overflow-hidden">
            <div className="h-full rounded-selo bg-insigne-orange" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {/* LER E SALVAR NA MESMA FILEIRA: a ação principal ocupa o que sobra e o
          marcador fica colado nela, quadrado. "Continuar" quando já existe
          progresso — aí a pessoa não vai começar, vai voltar. */}
      <div className="mt-3 flex gap-2">
        <Link
          to={main + "ler/" + ebookId}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="foco-laranja flex flex-1 items-center justify-center gap-2 rounded-lg bg-insigne-orange
                     px-4 py-2.5 text-sm font-bold text-paper transition-colors hover:bg-insigne-orange-dark"
        >
          <i className="bi bi-book" /> {progresso && pct > 0 ? "Continuar" : "Ler"}
        </Link>

        <button
          type="button"
          onClick={alternar}
          onMouseEnter={() => setSobre(true)}
          onMouseLeave={() => setSobre(false)}
          onFocus={() => setSobre(true)}
          onBlur={() => setSobre(false)}
          disabled={salvar.parado || ocupado}
          title={salvar.titulo}
          aria-label={salvar.titulo}
          aria-pressed={!!situacao?.salvo}
          className={
            "foco-laranja inline-flex w-11 shrink-0 items-center justify-center rounded-lg border " +
            "text-base transition-colors disabled:cursor-default " +
            (salvar.marcado
              ? "border-insigne-orange bg-insigne-orange/15 text-insigne-orange"
              : "border-insigne-orange/50 text-insigne-orange hover:bg-insigne-orange/10 disabled:opacity-50")
          }
        >
          <i className={"bi " + (ocupado ? "bi-arrow-repeat animate-spin" : salvar.icone)} />
        </button>
      </div>

      {/* A página do livro NA LOJA: quem lê pelo Clube e quer o arquivo, ou o
          impresso, encontra os preços ali. */}
      <Link
        to={main + "livros/" + ebookId}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="foco-laranja mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border
                   border-white/15 bg-white/5 px-3 py-2 text-[13px] font-semibold leading-tight
                   text-white/80 transition-colors hover:border-white/30 hover:bg-white/10 hover:text-white"
      >
        <i className="bi bi-tag shrink-0" /> Ver preços
      </Link>
    </div>
  );
};

export default CartaoDoClube;
